import { actionColumn } from "@/components/Table/table.interface";
import { MODULES } from "@/constants";
import { Channel } from "@/types";
import parseJSON from "@/utils/parseJSON";
import { capitalize } from "lodash";
import {
  CreativeType,
  getAssociatedLineItemsCount,
  getCreativeType,
  internalLinkedinCreativeToInternalCreativeMapping,
} from "../CreateOrEditAsset/data";
import { AdFormatTypeUnion } from "../CreateOrEditAsset/types";

/**
 * `assetInfo` arrives as a JSON STRING on the row and has to be parsed before
 * either Size or Type can be read. Both the cell and the column's `sortKey` go
 * through here, so neither can end up reading a different shape than the other.
 */
export const creativeAssetInfo = (creative: any) =>
  creative && typeof creative.assetInfo === "string"
    ? parseJSON(creative.assetInfo)
    : creative?.assetInfo;

/** The dimensions the Size cell prints, e.g. "300 x 250". */
export const creativeSizeLabel = (creative: any) => {
  const dimensions = creativeAssetInfo(creative)?.dimentions;
  return dimensions ? `${dimensions.width} x ${dimensions.height}` : null;
};

/**
 * Size sorts on the printed string, which `sorting.ts` compares with numeric
 * collation — so "300 x 250" precedes "1200 x 628" on width, and "300 x 250"
 * precedes "300 x 600" on height once the widths tie. Empty for a creative with
 * no dimensions, whose cell shows the "_" placeholder, so those sort last rather
 * than grouping under an underscore.
 */
export const creativeSizeSortKey = (creative: any) =>
  creativeSizeLabel(creative) ?? "";

/**
 * The word the Type cell prints. Moved here from the row so the column can order
 * by it — its id names no field, which is why the column was `noSort`.
 *
 * Reads a creative differently per channel, which is why the header array is
 * built with the channel closed over rather than declared at module scope.
 */
export const creativeTypeLabel = (asset: any, channel: Channel) => {
  if (channel === "display") {
    // VAST wrapper has no uploaded asset — identified by the tag URL.
    if (asset?.vastTagUrl) return "VAST Wrapper";
    if (asset?.CreativeTemplateDataValueMapping?.id) return "JS Tag";
    if (asset?.assetInfo && asset?.assetInfo?.type) {
      const assetType = asset?.assetInfo?.type;
      if (assetType.includes("text")) return assetType?.split("/")?.[1];
      if (asset?.nativeOfferId) return "native";
      return asset?.assetInfo.mimeType;
    }
    if (asset?.creativeRuleKey && asset?.creativeContentTag) return "JS Tag";
    return undefined;
  }
  if (channel === "facebook") {
    if (asset?.image_hash) return CreativeType.IMAGE;
    if (asset?.video_id) return CreativeType.VIDEO;
    return undefined;
  }
  return capitalize(
    internalLinkedinCreativeToInternalCreativeMapping[
      asset?.type as AdFormatTypeUnion
    ]
  );
};

/**
 * Type resolves through the parsed `assetInfo`, the same object the cell hands
 * it — the raw row carries the unparsed string, so passing the row straight in
 * would fail every `assetInfo.type` test.
 */
const creativeTypeSortKey = (channel: Channel) => (creative: any) =>
  creativeTypeLabel(
    { ...creative, assetInfo: creativeAssetInfo(creative) },
    channel
  ) ?? "";

/**
 * Size and Type were `noSort` because their ids name no field on the row, and
 * Associations sorted `row["lineItemAttached"]` — also absent, since the count
 * comes from a per-channel associations array. All three now name their value.
 */
/**
 * Whether this creative can carry a click URL at all.
 *
 * Resolved through `getCreativeType` — the SAME function `EditAsset` uses to
 * seed its form — against the same exemption list `asset.validation.ts` applies
 * to `clickUrl`. Going through that function rather than reading the row is the
 * point: inline editing and the Edit Asset form cannot drift apart about which
 * creatives take a click URL, because they ask the same question of the same
 * code.
 *
 * Do NOT reach for `creativeTypeLabel` here. It returns what the Type COLUMN
 * prints — "VAST Wrapper", "JS Tag", a mime subtype, a capitalised LinkedIn
 * label — and has no branch at all for HTML or bulk tags, so an HTML creative
 * would be offered a field the backend then rejects.
 *
 * It needs the parsed `assetInfo` for the same reason the Type column does.
 */
const NO_CLICK_URL_TYPES = [
  CreativeType.HTML,
  CreativeType.JS_TAG,
  CreativeType.TEXT,
  CreativeType.VAST,
  CreativeType.BULK_TAGS,
];

export const creativeTakesClickUrl = (creative: any) => {
  const creativeType = getCreativeType({
    ...creative,
    assetInfo: creativeAssetInfo(creative),
  });
  return !NO_CLICK_URL_TYPES.includes(creativeType as CreativeType);
};

export const listColumns = (channel: Channel) => [
  { id: "name", label: "Name", width: 300, wrap: true, grow: true },
  // Editable in the table's edit mode. `width` is not optional here: every
  // header in this array declares one, and CustomTable honours declared widths
  // only when ALL of them do — a width-less column silently disables sizing for
  // the whole table.
  {
    id: "clickUrl",
    label: "Click URL",
    width: 260,
    wrap: true,
    editable: true,
  },
  // No Channel column. Every list built from this array is already scoped to one
  // channel — it is a parameter of this very function — so the cell drew the
  // same logo on every row and the column spent 110px saying what the page had
  // established before you got to it. (The GROUPED list is different: its rows
  // genuinely vary, so its own Channel column stays.)
  {
    id: "lineItemAttached",
    label: "Associations",
    width: 140,
    sortKey: getAssociatedLineItemsCount,
  },
  { id: "size", label: "Size", width: 110, sortKey: creativeSizeSortKey },
  // `noSort`: approvals are fetched per row inside AssetTableRow
  // (`useListAdCreativeApprovals`), so nothing named `vendorApproval` reaches
  // the row data the table sorts. It was offering a control that lit up and
  // never moved anything.
  { id: "vendorApproval", label: "Vendor Approval", width: 160, noSort: true },
  // Stays `noSort`: the cell is an image, so there is nothing to order by.
  {
    id: "thumbnail",
    label: "Thumbnail",
    noSort: true,
    width: 140,
    noTruncate: true,
  },
  { id: "type", label: "Type", width: 110, sortKey: creativeTypeSortKey(channel) },
  actionColumn({ module: MODULES.CREATIVES, actions: ["update", "delete"] }),
];
